import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs/Subject';
import {MachinesService} from '../../../services/machines.service';
import {Machine} from '../../../models/machine.model';
import {ConfirmationService, SelectItem} from 'primeng/primeng';
import {AuthenticationService} from '../../../services/authentication.service';
import {MachineMaintenance} from '../../../models/machine.maintenance.model';
import {Maintenance} from '../../../models/maintenance.model';
import {MachineMaintenanceService} from '../../../services/machine.maintenance.service';
import {Apointment} from '../../../models/apointment.model';
import {AgendaService} from '../../../services/agenda.service';
import {MaintenanceService} from '../../../services/maintenance.service';
import {CustomerService} from '../../../services/customers.service';

@Component({
  providers: [MachinesService, MachineMaintenanceService, MaintenanceService, AgendaService, CustomerService],
  selector: 'app-machines-details',
  styleUrls: ['./machine.details.component.scss'],
  templateUrl: './machine.details.component.html',
})
export class MachineDetailsComponent implements OnDestroy, OnInit {
  public machine: Machine;
  public readOnly = true;

  // nieuwe afspraak
  public displayNewApointment = false;
  public newError: string;
  public maintenanceTypes: SelectItem[];
  public selectedMaintenance: string;
  public workhours = 0;
  public travelhours = 0;
  public traveldistance = 0;
  public newDate: Date;

  private _ngUnSubscribe: Subject<void> = new Subject<void>();

  constructor(private _route: ActivatedRoute, private _machineService: MachinesService,
              public router: Router, private confirmationService: ConfirmationService,
              private _authService: AuthenticationService, private _machineMaintenanceService: MachineMaintenanceService,
              private _maintenanceService: MaintenanceService, private _agendaService: AgendaService,
              private _customerService: CustomerService) {}

  public ngOnInit() {
    this.maintenanceTypes = [];
    this.maintenanceTypes.push({label: 'Regulier onderhoud', value: 'Regular'});
    this.maintenanceTypes.push({label: 'Incident', value: 'Incident'});

    this._route.params
      .takeUntil(this._ngUnSubscribe)
      .subscribe(params => {
        const machineId = params['id'];

        if (machineId) {
          this._machineService.load(machineId);
          this._machineService.machines$
            .takeUntil(this._ngUnSubscribe)
            .subscribe(
              machines => {
                this.machine = machines.find(pr => pr._id === machineId);
                if (this.machine && this.machine.active) {
                  this.initNewMaintenance();
                }
              }
            );
        }
      });
  }

  public ngOnDestroy() {
    this._ngUnSubscribe.next();
    this._ngUnSubscribe.complete();
  }

  public isIncident(): boolean {
    return (this.selectedMaintenance === 'Incident');
  }

  public historyMachine(): void {
    this.router.navigate(['history'], {relativeTo: this._route});
  }

  public editMachine(): void {
    this.router.navigate(['edit'], {relativeTo: this._route});
  }

  public deleteMachine(): void {
    this.confirmationService.confirm({
      message: 'Weet u het zeker dat u de machine wilt verwijderen?',
      accept: () => {
        const customerId = this.machine.customer;
        this.machine.active = false;
        this._machineService.update(this.machine)
          .subscribe(result => {
            this.router.navigate(['/customers', customerId]);
          });
      }
    });
  }

  public printMachine(divName): void {
    const printContents = document.getElementById(divName).innerHTML;
    const popupWin = window.open('', '_blank');
    popupWin.document.open()
    popupWin.document.write('<html><head></head><body onload="window.print()">' + printContents + '</html>');
    popupWin.document.close();
  }

  public isAdmin(): boolean {
    if (this._authService.user && this._authService.user.role === 'admin') {
      return true;
    }

    return false;
  }

  public saveMaintenance(): void {
    this._machineMaintenanceService.update(this.machine.currentMachineMaintenance);
    this.router.navigate(['/customers', this.machine.customer._id]);
  }

  public startMaintenance(): void {
    if (!this._authService.user) {
      return;
    }

    const newMaintenance: Maintenance = new Maintenance();
    newMaintenance.created = new Date();
    newMaintenance.finished = false;
    newMaintenance.customer = this.machine.customer;
    newMaintenance.user = this._authService.user._id;

    this._maintenanceService.create(newMaintenance)
      .subscribe(maintenance => {
        if (maintenance) {
          this.machine.customer.currentMaintenance = maintenance;
          this._customerService.update(this.machine.customer);
          this.initNewMaintenance();
        }
      });
  }

  public finishMaintenance(): void {
    this.setApointmentSettings();
  }

  public saveNewApointment(): void {
    this._machineMaintenanceService.update(this.machine.currentMachineMaintenance)
      .then(machineMaintenance => {
        return this._maintenanceService.load(this.machine.customer.currentMaintenance._id);
      })
      .then(maintenance => {
        maintenance.finished = true;
        maintenance.workhours = this.workhours;
        maintenance.travelhours = this.travelhours;
        maintenance.distance = this.traveldistance;
        maintenance.name = this.selectedMaintenance;
        this._maintenanceService.update(maintenance)
          .subscribe(maint => {
            this._maintenanceService.sendMail(maintenance._id);

            if (this.selectedMaintenance === 'Regular') {
              const apointment = new Apointment();
              apointment.title = `${this.machine.customer.name} onderhoud`;
              apointment.customer = this.machine.customer;

              const day = this.newDate.getDate();
              let dayString = '';
              if (day < 10) {
                dayString = `0${day}`;
              } else {
                dayString = `${day}`;
              }

              const month = this.newDate.getMonth() + 1;
              let monthString = '';
              if (month < 10) {
                monthString = `0${month}`;
              } else {
                monthString = `${month}`;
              }

              const year = this.newDate.getFullYear();
              const dateString = `${year}-${monthString}-${dayString}T00:00:00.000Z`;

              apointment.date = dateString;
              this._agendaService.create(apointment);
            }

            this.setApointmentSettings();
            this.displayNewApointment = false;
            this.readOnly = true;
          }, error => {
            this.setApointmentSettings();
            this.displayNewApointment = false;
          });
      });
  }

  private setApointmentSettings(): void {
    this.displayNewApointment = true;
    this.selectedMaintenance = 'Regular';
    this.newError = null;
    this.workhours = 0;
    this.travelhours = 0;
    this.traveldistance = 0;
    this.newDate = new Date();
  }

  private initNewMaintenance(): void {
    const customer = this.machine.customer;
    if (!customer) {
      return;
    }

    if (customer.currentMaintenance && !customer.currentMaintenance.finished) {
      //check if the current maintenance of the customer is the maintenance in the machine, otherwise create a new one
      if (!this.machine.currentMachineMaintenance || this.machine.currentMachineMaintenance.maintenance != customer.currentMaintenance._id) {
        let oldMachineMaintenance: MachineMaintenance;
        if (this.machine.currentMachineMaintenance) {
          oldMachineMaintenance = this.machine.currentMachineMaintenance;
        }

        const newMachineMaintenance = this.createNewMachineMaintenance(this.machine, this.machine.customer.currentMaintenance, oldMachineMaintenance);
        this._machineMaintenanceService.create(newMachineMaintenance)
          .subscribe(machineMaintenance => {
            this.machine.currentMachineMaintenance = machineMaintenance;
          });
      }

      this.readOnly = false;
    }
  }

  private createNewMachineMaintenance(machine: Machine, maintenance: Maintenance, oldMachineMaintenance: MachineMaintenance): any {
    const newMachineMaintenance = new MachineMaintenance();
    newMachineMaintenance.machine = machine._id;
    newMachineMaintenance.maintenance = maintenance._id;
    if (oldMachineMaintenance) {
      newMachineMaintenance.hours = oldMachineMaintenance.hours;
    }

    return newMachineMaintenance;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/machine/details/machine.details.component.ts